import React from 'react';
import { Box, Typography, LinearProgress, Fade, keyframes } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { AskJobStatus } from '../../../Types/Docs';

const pulseAnimation = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.08); opacity: 0.85; }
`;

const shimmerAnimation = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const STATUS_LABELS: Record<AskJobStatus, string> = {
  queued: 'Queued — waiting for the agent',
  running: 'Running — searching your knowledge base',
  done: 'Completed',
  failed: 'Failed',
};

interface QueryLoadingOverlayProps {
  query: string;
  status?: AskJobStatus;
  logs?: string[];
  'data-testid'?: string;
}

const QueryLoadingOverlay: React.FC<QueryLoadingOverlayProps> = ({
  query,
  status = 'running',
  logs = [],
  'data-testid': testId = 'docs-query-loading-overlay',
}) => {
  const statusLabel = STATUS_LABELS[status] ?? STATUS_LABELS.running;
  const recentLogs = logs.slice(-5);

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Searching documents"
      data-testid={testId}
      sx={{
        p: 4,
        mb: 3,
        borderRadius: '0.5rem',
        border: '1px solid #D9D9D9',
        bgcolor: '#FFFFFF',
        boxShadow: '-1px 2px 12px rgba(11, 147, 223, 0.12)',
      }}
    >
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: '#FFFFFF',
            animation: `${pulseAnimation} 1.6s ease-in-out infinite`,
          }}
          aria-hidden="true"
        >
          <AutoAwesomeIcon />
        </Box>
        <Box>
          <Typography variant="h6" component="p" sx={{ fontWeight: 600, mb: 0.5 }}>
            {statusLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary" data-testid={`${testId}-query`}>
            &ldquo;{query}&rdquo;
          </Typography>
        </Box>
      </Box>

      <LinearProgress
        variant="indeterminate"
        aria-label="Search progress"
        data-testid={`${testId}-progress`}
        sx={{
          height: 6,
          borderRadius: 3,
          mb: 3,
          bgcolor: '#E8F4FC',
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            background: 'linear-gradient(90deg, #0B93DF 0%, #4DB8F0 50%, #0B93DF 100%)',
            backgroundSize: '200% 100%',
            animation: `${shimmerAnimation} 1.8s linear infinite`,
          },
        }}
      />

      {recentLogs.length > 0 && (
        <Box
          component="ul"
          sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}
          data-testid={`${testId}-logs`}
        >
          {recentLogs.map((log, index) => (
            <Fade in key={`${log}-${index}`} timeout={300}>
              <Box component="li">
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {log}
                </Typography>
              </Box>
            </Fade>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default QueryLoadingOverlay;
